// src/components/ui/modals/ConfirmDialog.tsx
import React from "react";
import { AlertTriangle, Info, AlertCircle, X } from "lucide-react";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import DangerButton from "../../buttons/DangerButton";

export type ConfirmDialogVariant = "info" | "warning" | "danger";

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  loading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "info",
  loading = false,
}) => {
  if (!open) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && !loading) {
      onClose();
    }
    if (e.key === "Enter" && !loading) {
      handleConfirm();
    }
  };

  const getIcon = () => {
    switch (variant) {
      case "danger":
        return <AlertCircle className="text-status-error" size={24} />;
      case "warning":
        return <AlertTriangle className="text-status-warning" size={24} />;
      case "info":
      default:
        return <Info className="text-status-info" size={24} />;
    }
  };

  const getConfirmButton = () => {
    switch (variant) {
      case "danger":
        return (
          <DangerButton onClick={handleConfirm} disabled={loading}>
            {loading ? "Processing..." : confirmText}
          </DangerButton>
        );
      case "warning":
      case "info":
      default:
        return (
          <PrimaryButton onClick={handleConfirm} disabled={loading}>
            {loading ? "Processing..." : confirmText}
          </PrimaryButton>
        );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-overlay"
      onKeyDown={handleKeyDown}
    >
      <div
        className="w-full max-w-md p-6 bg-bg-surface rounded-xl shadow-elev-3 relative border border-border-default"
        tabIndex={-1}
        aria-modal="true"
        role="alertdialog"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-text-muted hover:text-text-primary transition-colors"
          onClick={onClose}
          aria-label="Close dialog"
          type="button"
          disabled={loading}
        >
          <X size={20} />
        </button>

        {/* Icon and Title */}
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 mt-1">{getIcon()}</div>
          <div className="flex-1">
            <h3
              id="confirm-dialog-title"
              className="text-lg font-semibold text-text-primary mb-2"
            >
              {title}
            </h3>
            <p
              id="confirm-dialog-description"
              className="text-sm text-text-secondary"
            >
              {message}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <SecondaryButton onClick={onClose} disabled={loading}>
            {cancelText}
          </SecondaryButton>
          {getConfirmButton()}
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
